import { Apple, Coins, ExternalLink } from "lucide-react";
import { dictionaries } from "../lib/i18n";
import { placeLinks } from "../utils/placeLink";
import type { LocalProducer, UiLang } from "../lib/types";
import { PlaceAltLink } from "./PlaceAltLink";

/**
 * Selvbetjente boder og setrer i nærheten av stoppet.
 *
 * Disse stedene står ikke i noen reisehåndbok og er ikke skiltet fra
 * hovedveien. En eplebod i Hardanger er en kasse, en kurv og et Vipps-nummer
 * på en lapp, og den er der enten noen kommer eller ikke. For en tilreisende
 * er det det nærmeste man kommer stedet slik det faktisk er.
 *
 * `selfService` får sitt eget merke og ikke bare en setning i teksten, fordi
 * det endrer hva man må ha med seg. En ubemannet bod tar kontanter eller
 * Vipps, og Vipps krever norsk bankkonto og BankID — så for den som kommer
 * utenfra kan en åpen bod være like utilgjengelig som en stengt butikk hvis
 * man ikke visste det før man kjørte forbi minibanken.
 *
 * Sesongen står alltid. De aller fleste av disse er åpne i noen få uker.
 */

interface Props {
  producers: LocalProducer[];
  lang: UiLang;
  /** Stoppet produsentene ligger ved. Brukes bare i søkelenka. */
  near?: string;
}

export function ProducerPanel({ producers, lang, near }: Props) {
  const t = dictionaries[lang];
  if (producers.length === 0) return null;

  return (
    <ul className="producers">
      {producers.map((producer) => (
        <li className="producer" key={producer.name}>
          <div className="producer-head">
            <Apple size={13} />
            <strong>{producer.name}</strong>
            <span className="producer-kind">{t.producerKind[producer.kind]}</span>
            {producer.selfService && (
              <span className="producer-self">
                <Coins size={11} />
                {t.producerSelfService}
              </span>
            )}
          </div>
          <p className="producer-note">{producer.note}</p>
          <div className="producer-foot">
            <span className="producer-season">{producer.season}</span>
            {/*
              Også de uten egen nettside skal kunne slås opp. En gårdsutsalg
              med en bod ved veien har sjelden en side, men den finnes på kartet
              — og «Syse gard» alene finner ikke det samme som «Syse gard
              Ulvik». Se utils/placeLink.ts.
            */}
            {(() => {
              const links = placeLinks(producer, near);
              return (
                <>
                  <a
                    className="producer-link no-print"
                    href={links.primary.href}
                    target="_blank"
                    rel="noreferrer"
                    title={producer.name}
                  >
                    {links.primary.host ?? t.producerFind}
                    <ExternalLink size={10} />
                  </a>
                  {/*
                    Et gårdsutsalg med en side laget én gang i 2016 er akkurat
                    den slags lenke som slutter å svare uten at noen merker det.
                    Se PlaceAltLink.
                  */}
                  <PlaceAltLink link={links.fallback} lang={lang} />
                </>
              );
            })()}
          </div>
        </li>
      ))}
    </ul>
  );
}
